<!-- Stacked bar chart of sign requirements (Required / Recommended / Optional) with sign icons on the x-axis. -->
<script lang="ts">
	import W1_1 from '#lib/assets/signs/W1-1.svg?raw';
	import W1_2 from '#lib/assets/signs/W1-2.svg?raw';
	import W1_3 from '#lib/assets/signs/W1-3.svg?raw';
	import W1_4 from '#lib/assets/signs/W1-4.svg?raw';
	import W1_5 from '#lib/assets/signs/W1-5.svg?raw';
	import W1_11 from '#lib/assets/signs/W1-11.svg?raw';

	type Segment = 'Required' | 'Recommended' | 'Optional';
	type BarDataItem = { name: string } & Record<Segment, number>;
	interface Props {
		data: BarDataItem[];
		colours: { key: string; color: string }[];
		width: number;
		height: number;
	}

	const { data, colours, width, height }: Props = $props();

	const signs: Record<string, string> = {
		'W1-1': W1_1,
		'W1-2': W1_2,
		'W1-3': W1_3,
		'W1-4': W1_4,
		'W1-5': W1_5,
		'W1-11': W1_11
	};
	const signWidth = 32;
	const signHeight = signWidth + 12;

	const margin = { top: 14, right: 0, left: -10, bottom: 16 };
	const xAxisHeight = 42;

	const maxValue = $derived(
		Math.max(1, ...data.map((d) => d.Required + d.Recommended + d.Optional))
	);

	// Nice integer step so roundedMax = step · numYTicks fits the data snugly (e.g. 112 → 120 not 200).
	const numYTicks = 3;
	const niceStep = $derived.by(() => {
		const rawStep = maxValue / numYTicks;
		const magnitude = 10 ** Math.floor(Math.log10(rawStep));
		return Math.max(1, Math.ceil(rawStep / magnitude) * magnitude);
	});
	const roundedMax = $derived(niceStep * numYTicks);
	const yTicks = $derived(Array.from({ length: numYTicks + 1 }, (_, i) => i * niceStep));

	const yLabelWidth = $derived(
		(
			[
				[1e6, 64],
				[1e5, 62],
				[1e4, 57],
				[1e3, 52]
			] as const
		).find(([threshold]) => roundedMax > threshold)?.[1] ?? 48
	);

	const plotX = $derived(yLabelWidth + margin.left);
	const plotY = margin.top;
	const plotWidth = $derived(width - yLabelWidth - margin.right - margin.left);
	const plotHeight = $derived(height - margin.top - margin.bottom - xAxisHeight);

	const groupW = $derived(plotWidth / data.length - 1);
	const barW = $derived(groupW * 0.56);

	function yPx(value: number): number {
		return plotY + plotHeight - (value / roundedMax) * plotHeight;
	}

	function barX(index: number): number {
		return plotX + index * groupW + (groupW - barW) / 2;
	}

	// Stack bottom to top in `colours` order, each segment ending at the running total.
	function segments(bar: BarDataItem) {
		let top = 0;
		return colours.map(({ key, color }) => {
			const value = bar[key as Segment];
			top += value;
			return { key, color, value, top };
		});
	}

	const descId = $props.id();
	const description = $derived(
		data
			.map(
				(d) =>
					`${d.name}: ${d.Required} required, ${d.Recommended} recommended, ${d.Optional} optional`
			)
			.join('; ')
	);
</script>

<svg
	{width}
	{height}
	role="img"
	aria-label="Horizontal alignment sign requirements"
	aria-describedby={descId}>
	<desc id={descId}>{description}</desc>
	{#each yTicks as tick (tick)}
		<line
			x1={plotX}
			x2={plotX + plotWidth}
			y1={yPx(tick)}
			y2={yPx(tick)}
			class="gridLine"
			stroke-dasharray="3 3" />
		<text
			x={yLabelWidth - 16}
			y={yPx(tick)}
			text-anchor="end"
			dominant-baseline="middle"
			class="tickLabel">
			{tick.toLocaleString('en-US')}
		</text>
	{/each}

	{#each data as bar, i (bar.name)}
		{@const x = barX(i)}
		{#each segments(bar) as s (s.key)}
			{#if s.value > 0}
				<rect
					{x}
					y={yPx(s.top)}
					width={barW}
					height={(s.value / roundedMax) * plotHeight}
					fill={s.color} />
			{/if}
		{/each}
		<svg x={x + barW / 2 - 16} y={plotY + plotHeight + 4} width={signWidth} height={signHeight}>
			<g transform="translate(7, 0)">{@html signs[bar.name]}</g>
			<text x={signWidth / 2 + 1} y={signHeight - 12} dy={5} text-anchor="middle" class="tickLabel">
				{bar.name}
			</text>
		</svg>
	{/each}
</svg>

<style>
	.gridLine {
		stroke: var(--border-color);
	}

	.tickLabel {
		fill: var(--text-color);
		font-size: var(--font-size-xs);
	}
</style>
